import apiService, { getToken } from './api';
import axios from 'axios';
import type { ApiResponse } from '../types';
import type { Numeric, NumericValue } from '@/utils/numeric';

export type TimesheetApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface TimesheetEntry {
  id: number;
  user_id: number;
  organisation_id: number;
  clock_in_time: string;
  clock_out_time?: string;
  date: string;
  total_hours?: NumericValue;
  notes?: string;
  status: 'clocked_in' | 'clocked_out';
  payroll_category?: string;
  holiday_name?: string | null;
  clock_in_latitude?: Numeric;
  clock_in_longitude?: Numeric;
  clock_in_accuracy?: number | null;
  clock_out_latitude?: Numeric;
  clock_out_longitude?: Numeric;
  clock_out_accuracy?: number | null;
  skip_lunch_deduction?: boolean;
  /** Set when a supervisor confirmed they asked for the worked-through lunch. */
  lunch_worked_approved_at?: string | null;
  lunch_worked_approved_by_name?: string | null;
  /** Employee claimed these hours off their work schedule — no clock record exists. */
  missed_clock?: boolean;
  missed_clock_reason?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PublicHolidayInfo {
  date: string;
  name: string;
}

export interface ApprovalEntriesResponse {
  entries: TimesheetEntry[];
  public_holidays_unworked: PublicHolidayInfo[];
}

export interface PayrollPreviewCategory {
  category: string;
  hours: NumericValue;
}

export interface PayrollPreviewItem {
  approval_id: number;
  user_id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  pay_frequency: 'weekly' | 'fortnightly' | null;
  start_date: string;
  end_date: string;
  categories: PayrollPreviewCategory[];
}

export interface PayrollPreviewResponse {
  previews: PayrollPreviewItem[];
  public_holidays: PublicHolidayInfo[];
  public_holidays_seeded: boolean;
}

export interface TimesheetApprovalRequest {
  id: number;
  user_id: number;
  organisation_id: number;
  start_date: string;
  end_date: string;
  total_hours: NumericValue;
  timesheet_entries_count: number;
  status: TimesheetApprovalStatus;
  submitted_at: string;
  approved_by?: number;
  approved_by_username?: string;
  approved_at?: string;
  approved_on_behalf_of_user_id?: number | null;
  approved_on_behalf_of_username?: string | null;
  supervisor_username?: string | null;
  rejection_reason?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  username?: string;
  first_name?: string | null;
  last_name?: string | null;
  myob_card_id?: string | null;
  pay_frequency?: 'weekly' | 'fortnightly' | null;
  email?: string;
  /** Payroll-by-API lifecycle — see `payrollPosting.ts`. Null = awaiting payroll review. */
  payroll_status?: PayrollStatus | null;
  payroll_provider?: 'xero' | 'myob' | null;
  payroll_approved_at?: string | null;
  posted_at?: string | null;
  post_error?: string | null;
  payroll_completed_at?: string | null;
}

export type PayrollStatus = 'payroll_approved' | 'posting' | 'posted' | 'post_failed' | 'completed';

export interface SubmitTimesheetApprovalRequest {
  start_date: string;
  end_date: string;
  notes?: string;
}

export interface ScheduledDay {
  date: string;            // YYYY-MM-DD
  scheduled_hours: number; // net of lunch
  start_time: string;      // HH:mm
  end_time: string;        // HH:mm
}

export interface LeaveDay {
  date: string;            // YYYY-MM-DD
  leave_type: string;
  status: 'pending' | 'approved';
  request_id: number;
}

export interface PeriodPreviewResponse {
  start_date: string;
  end_date: string;
  scheduled_working_days: number;
  scheduled_hours: number;
  scheduled_days: ScheduledDay[];
}

class TimesheetApprovalService {
  // Submit timesheet for approval
  async submitForApproval(data: SubmitTimesheetApprovalRequest): Promise<ApiResponse<TimesheetApprovalRequest>> {
    return apiService.post('/timesheet/approval/submit', data);
  }

  // Get user's timesheet submissions
  async getSubmissions(params: {
    status?: TimesheetApprovalStatus;
  } = {}): Promise<ApiResponse<TimesheetApprovalRequest[]>> {
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = queryString ? `/timesheet/approval/submissions?${queryString}` : '/timesheet/approval/submissions';

    return apiService.get(url);
  }

  // User: Recall (cancel) their own pending submission
  async recallSubmission(id: number): Promise<ApiResponse<TimesheetApprovalRequest>> {
    return apiService.patch(`/timesheet/approval/requests/${id}/recall`, {});
  }

  // Supervisor: Get pending approvals count
  async getPendingApprovalsCount(): Promise<ApiResponse<{ count: number }>> {
    return apiService.get('/timesheet/approval/supervisor/pending-count');
  }

  // Supervisor: Get approvals for supervised employees.
  // `scope: 'hr'` (HR licence holders only) returns every pending org-wide
  // request — used by the HR Override tab.
  async getSupervisorApprovals(params: {
    status?: TimesheetApprovalStatus;
    scope?: 'mine' | 'hr';
  } = {}): Promise<ApiResponse<TimesheetApprovalRequest[]>> {
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);
    if (params.scope) queryParams.append('scope', params.scope);

    const queryString = queryParams.toString();
    const url = queryString ? `/timesheet/approval/supervisor/approvals?${queryString}` : '/timesheet/approval/supervisor/approvals';

    return apiService.get(url);
  }

  // Supervisor: Get timesheet entries for a specific approval request.
  // The backend returns { success, data: TimesheetEntry[], public_holidays_unworked, scheduled_working_days, scheduled_hours }.
  async getApprovalEntries(id: number): Promise<ApiResponse<TimesheetEntry[]> & {
    public_holidays_unworked?: PublicHolidayInfo[];
    scheduled_working_days?: number;
    scheduled_hours?: number;
    scheduled_days?: ScheduledDay[];
    leave_days?: LeaveDay[];
  }> {
    return apiService.get(`/timesheet/approval/requests/${id}/entries`);
  }

  // Preview scheduled working days / hours for a date range — used by the
  // submission form to show users what they were rostered for.
  async getPeriodPreview(start_date: string, end_date: string): Promise<ApiResponse<PeriodPreviewResponse>> {
    const params = new URLSearchParams({ start_date, end_date });
    return apiService.get(`/timesheet/period-preview?${params.toString()}`);
  }

  // Payroll Manager: Get category-level preview of what will be exported,
  // plus the list of public holidays seeded in the period.
  async getPayrollPreview(params: {
    start_date: string;
    end_date: string;
    pay_frequency?: string;
  }): Promise<ApiResponse<PayrollPreviewResponse>> {
    const queryParams = new URLSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
    });
    if (params.pay_frequency) queryParams.set('pay_frequency', params.pay_frequency);
    return apiService.get(`/timesheet/approval/payroll/preview?${queryParams.toString()}`);
  }

  // Supervisor: Review timesheet approval request
  async reviewRequest(id: number, data: {
    status: 'approved' | 'rejected';
    rejection_reason?: string;
  }): Promise<ApiResponse<TimesheetApprovalRequest>> {
    return apiService.patch(`/timesheet/approval/requests/${id}/review`, data);
  }

  // Supervisor / HR: record (or withdraw) that they asked this person to work
  // through lunch. Working through the meal break attracts penalty rates.
  async setLunchApproval(entryId: number, approved: boolean): Promise<ApiResponse<{
    id: number;
    skip_lunch_deduction: boolean;
    lunch_worked_approved_by: number | null;
    lunch_worked_approved_at: string | null;
  }>> {
    return apiService.post(`/timesheet/${entryId}/lunch-approval`, { approved });
  }

  // Payroll Manager: Get all approved timesheets org-wide for a date range
  async getPayrollApprovedTimesheets(params: {
    start_date: string;
    end_date: string;
    pay_frequency?: string;
    organisation_id?: number;
  }): Promise<ApiResponse<TimesheetApprovalRequest[]>> {
    const queryParams = new URLSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
    });
    if (params.pay_frequency) queryParams.set('pay_frequency', params.pay_frequency);
    if (params.organisation_id != null) queryParams.set('organisation_id', String(params.organisation_id));
    return apiService.get(`/timesheet/approval/payroll/approved?${queryParams.toString()}`);
  }

  // Payroll Manager: Download Xero CSV export for a date range
  async exportToXero(params: {
    start_date: string;
    end_date: string;
    pay_frequency?: string;
    organisation_id?: number;
  }): Promise<Blob> {
    const token = getToken();
    const baseURL =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
    const queryParams = new URLSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
    });
    if (params.pay_frequency) queryParams.set('pay_frequency', params.pay_frequency);
    if (params.organisation_id != null) queryParams.set('organisation_id', String(params.organisation_id));
    try {
      const response = await axios.get(
        `${baseURL}/timesheet/approval/payroll/export/xero?${queryParams.toString()}`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      return response.data as Blob;
    } catch (err: any) {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          throw new Error(json?.error?.message || json?.message || 'Export failed.');
        } catch (parseErr) {
          if (parseErr instanceof SyntaxError) throw new Error('Export failed.');
          throw parseErr;
        }
      }
      throw err;
    }
  }

  // Payroll Manager: Direct API upload to the org's connected MYOB AccountRight.
  // Hits the same endpoint as exportToMYOB but with `?method=api` so the server
  // POSTs the lines straight into the customer's company file instead of
  // returning a downloadable file.
  async uploadToMYOBApi(params: {
    start_date: string;
    end_date: string;
    pay_frequency?: string;
    organisation_id?: number;
  }): Promise<ApiResponse<{
    method: 'api';
    posted: number;
    failed: Array<{ employee: string; reason: string }>;
    total: number;
  }>> {
    const queryParams = new URLSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
      method: 'api',
    });
    if (params.pay_frequency) queryParams.set('pay_frequency', params.pay_frequency);
    if (params.organisation_id != null) queryParams.set('organisation_id', String(params.organisation_id));
    return apiService.get(`/timesheet/approval/payroll/export?${queryParams.toString()}`);
  }

  // Payroll Manager: Direct API upload to the org's connected Xero tenant.
  // Same relationship to exportToXero as uploadToMYOBApi has to exportToMYOB —
  // `?method=api` makes the server POST timesheets into Xero Payroll instead of
  // returning a CSV.
  //
  // The date range must line up with a Xero payroll calendar period or Xero
  // rejects the timesheet; derive it from `xeroPayrollService.listPayrollCalendars()`
  // rather than letting the user pick arbitrary dates.
  async uploadToXero(params: {
    start_date: string;
    end_date: string;
    pay_frequency?: string;
    organisation_id?: number;
  }): Promise<ApiResponse<{
    method: 'api';
    posted: number;
    failed: Array<{ employee: string; reason: string }>;
    total: number;
  }>> {
    const queryParams = new URLSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
      method: 'api',
    });
    if (params.pay_frequency) queryParams.set('pay_frequency', params.pay_frequency);
    if (params.organisation_id != null) queryParams.set('organisation_id', String(params.organisation_id));
    return apiService.get(`/timesheet/approval/payroll/export/xero?${queryParams.toString()}`);
  }

  // Payroll Manager: Download MYOB IIF/TXT export for a date range
  async exportToMYOB(params: {
    start_date: string;
    end_date: string;
    pay_frequency?: string;
    organisation_id?: number;
  }): Promise<Blob> {
    const token = getToken();
    const baseURL =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
    const queryParams = new URLSearchParams({
      start_date: params.start_date,
      end_date: params.end_date,
    });
    if (params.pay_frequency) queryParams.set('pay_frequency', params.pay_frequency);
    if (params.organisation_id != null) queryParams.set('organisation_id', String(params.organisation_id));
    try {
      const response = await axios.get(
        `${baseURL}/timesheet/approval/payroll/export?${queryParams.toString()}`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      return response.data as Blob;
    } catch (err: any) {
      // blob responseType means error bodies arrive as Blobs — parse them
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          throw new Error(json?.error?.message || json?.message || 'Export failed.');
        } catch (parseErr) {
          if (parseErr instanceof SyntaxError) throw new Error('Export failed.');
          throw parseErr;
        }
      }
      throw err;
    }
  }
}

export const timesheetApprovalService = new TimesheetApprovalService();
export default timesheetApprovalService;
